import React, { createContext, useContext, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface OnboardingStep {
  id: string
  title: string
  description: string
  completed: boolean
  path: string // Where to navigate when clicking "Complete Now"
  icon: string // Icon name for step
}

export interface OnboardingState {
  steps: OnboardingStep[]
  totalSteps: number
  completedSteps: number
  isComplete: boolean
  isDismissed: boolean
}

interface OnboardingContextType {
  state: OnboardingState
  isLoading: boolean
  isPanelOpen: boolean
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void
  dismissOnboarding: () => void
  restartOnboarding: () => void
  refreshProgress: () => void
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider')
  }
  return context
}

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const queryClient = useQueryClient()

  // Fetch onboarding progress from backend
  const { data: onboardingData, isLoading } = useQuery({
    queryKey: ['onboardingProgress'],
    queryFn: async () => {
      const response = await apiClient.request<{ success: boolean; data: OnboardingState }>(
        '/wp-json/redd-miner-leads/v1/onboarding/progress'
      )
      return response.data
    },
    staleTime: 30000,
    refetchInterval: 60000, // Refresh every minute to detect changes
  })

  // Dismiss onboarding mutation
  const dismissMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.request(
        '/wp-json/redd-miner-leads/v1/onboarding/dismiss',
        {
          method: 'POST',
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingProgress'] })
      setIsPanelOpen(false)
    },
  })

  // Restart onboarding mutation
  const restartMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.request(
        '/wp-json/redd-miner-leads/v1/onboarding/restart',
        {
          method: 'POST',
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingProgress'] })
      setIsPanelOpen(true)
    },
  })

  // Auto-open panel for new users with incomplete steps
  useEffect(() => {
    if (onboardingData && !onboardingData.isComplete && !onboardingData.isDismissed) {
      // Auto-open on first load if user hasn't dismissed it
      const hasSeenOnboarding = localStorage.getItem('onboarding_seen')
      if (!hasSeenOnboarding) {
        setIsPanelOpen(true)
        localStorage.setItem('onboarding_seen', 'true')
      }
    }
  }, [onboardingData])

  const defaultState: OnboardingState = {
    steps: [],
    totalSteps: 4,
    completedSteps: 0,
    isComplete: false,
    isDismissed: false,
  }

  const state = onboardingData || defaultState

  const value: OnboardingContextType = {
    state,
    isLoading,
    isPanelOpen,
    openPanel: () => setIsPanelOpen(true),
    closePanel: () => setIsPanelOpen(false),
    togglePanel: () => setIsPanelOpen(!isPanelOpen),
    dismissOnboarding: () => dismissMutation.mutate(),
    restartOnboarding: () => restartMutation.mutate(),
    refreshProgress: () => queryClient.invalidateQueries({ queryKey: ['onboardingProgress'] }),
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}
